import type {
  CmmdCloudProjectLifecyclePreflight,
  CmmdRuntimeDiagnosticsSnapshot,
  RuntimeDiagnosticsResponse,
} from "@cmmd-forge/contracts";

import { DiagnosticsCodexReadiness } from "./DiagnosticsCodexReadiness";
import { DiagnosticsEvidenceNotice } from "./DiagnosticsEvidenceNotice";
import {
  makeCloudControlPlaneSnapshot,
  type CloudRuntimeView,
} from "./DiagnosticsCloudRuntimes.logic";
import {
  DiagnosticsCard,
  DiagnosticsMetrics,
  DiagnosticsSection,
  StatusPill,
  formatTimestamp,
} from "./DiagnosticsPanelPrimitives";
import type { CodexRuntimeReadiness } from "./codexRuntimeReadiness";
import type { RuntimeAuthorityRenewalState } from "../../environments/runtime/cloudRuntimeAuthorityRenewal";
import {
  DiagnosticsCloudAdminEvidence,
  type CloudTraceDiagnostics,
} from "./DiagnosticsCloudAdminEvidence";
import { DiagnosticsProviderAuthority } from "./DiagnosticsProviderAuthority";
import {
  actualsFromDiagnostics,
  diffPreparedContract,
  expectedFromSnapshot,
} from "./preparedContractDiff";
import {
  lifecycleFromCatalogStatus,
  projectDiagnosticsSeatHealth,
  type DiagnosticsSeatHealth,
} from "./diagnosticsProductHealth";
import type { StatusTone } from "./DiagnosticsSettings.types";

export type { CloudTraceDiagnostics };

function contractRowsFromSnapshot(input: {
  readonly snapshot: CmmdRuntimeDiagnosticsSnapshot | undefined;
  readonly tools: ReadonlyArray<{ readonly name: string; readonly status: string }>;
}) {
  return diffPreparedContract({
    expected: expectedFromSnapshot(input.snapshot),
    actuals: actualsFromDiagnostics({
      guestForgeCommit: input.snapshot?.forge.installedCommit,
      supervisedServiceObserved: input.snapshot !== undefined,
      toolNames: input.tools.map((tool) => ({
        name: tool.name,
        ok: tool.status === "ok",
      })),
      replicationAvailable:
        input.snapshot === undefined ? null : input.snapshot.replication.state === "available",
    }),
  });
}

function seatHealthTone(health: DiagnosticsSeatHealth): StatusTone {
  if (health.state === "ready") return "ready";
  if (health.state === "paused") return "warning";
  if (health.state === "recover") return "error";
  return "muted";
}

function seatHolderMetrics(health: DiagnosticsSeatHealth) {
  return [
    { label: "Health", value: health.label },
    { label: "Detail", value: health.detail },
    ...(health.failedStage ? [{ label: "Failed stage", value: health.failedStage }] : []),
  ];
}

function operatorMetrics(snapshot: ReturnType<typeof makeCloudControlPlaneSnapshot>) {
  return [
    { label: "Observed", value: formatTimestamp(snapshot.observedAt) },
    { label: "Collection", value: snapshot.collectionOutcome.replaceAll("_", " ") },
    { label: "Catalog source", value: "forge-context" },
    { label: "Snapshot source", value: "CMMD forge-context" },
  ];
}

/**
 * One Environment, rendered as the same column of settings sections the server
 * target uses.
 *
 * Seat holders see product health only. Platform admins also see operator
 * evidence after Recheck.
 */
type CloudRuntimesProps = {
  readonly selectedRuntime: CloudRuntimeView | null;
  readonly canonicalSnapshot: CmmdRuntimeDiagnosticsSnapshot | undefined;
  readonly canonicalError: string | undefined;
  readonly canonicalPending: boolean;
  readonly lifecycleReceipt: CmmdCloudProjectLifecyclePreflight | undefined;
  readonly lifecycleError: string | undefined;
  readonly lifecyclePending: boolean;
  readonly codexReadiness: CodexRuntimeReadiness;
  readonly providerAuthority?: RuntimeAuthorityRenewalState | null;
  readonly traces: CloudTraceDiagnostics;
  readonly machineInventory?: {
    readonly inventory: RuntimeDiagnosticsResponse | undefined;
    readonly error: string | undefined;
    readonly pending: boolean;
    readonly probed: boolean;
  };
  readonly isPlatformAdmin: boolean;
};

export function DiagnosticsCloudRuntimes(props: CloudRuntimesProps) {
  if (!props.selectedRuntime) {
    return (
      <DiagnosticsSection
        title="Environment"
        description="No Environment is selected. Pick one from the target menu above."
      />
    );
  }
  return <DiagnosticsCloudRuntimeSelected {...props} selectedRuntime={props.selectedRuntime} />;
}

function DiagnosticsCloudRuntimeSelected({
  selectedRuntime,
  canonicalSnapshot,
  canonicalError,
  canonicalPending,
  lifecycleReceipt,
  lifecycleError,
  lifecyclePending,
  codexReadiness,
  providerAuthority,
  traces,
  machineInventory,
  isPlatformAdmin,
}: CloudRuntimesProps & { readonly selectedRuntime: CloudRuntimeView }) {
  const snapshot = makeCloudControlPlaneSnapshot({
    runtime: selectedRuntime,
    snapshot: canonicalSnapshot,
  });
  const boundSnapshot = snapshot.collectionOutcome === "matched" ? canonicalSnapshot : undefined;
  const lifecycle =
    selectedRuntime.lifecycle ?? lifecycleFromCatalogStatus(selectedRuntime.catalogStatus);
  const seatHealth = projectDiagnosticsSeatHealth({
    lifecycle,
    snapshot: boundSnapshot,
  });
  const contractRows = contractRowsFromSnapshot({
    snapshot: boundSnapshot,
    tools: machineInventory?.inventory?.tools ?? [],
  });

  return (
    <>
      <DiagnosticsSection
        title={selectedRuntime.label}
        id="diagnostics-target"
        description="Environment health from the Diagnostics snapshot. Looking does not wake this Sprite."
        action={<StatusPill label={seatHealth.label} tone={seatHealthTone(seatHealth)} />}
      >
        <div className="px-3 pb-2 sm:px-4">
          {isPlatformAdmin ? <DiagnosticsEvidenceNotice evidence="control-plane" /> : null}
        </div>
        <DiagnosticsCard>
          <DiagnosticsMetrics
            metrics={
              isPlatformAdmin
                ? [...seatHolderMetrics(seatHealth), ...operatorMetrics(snapshot)]
                : seatHolderMetrics(seatHealth)
            }
          />
        </DiagnosticsCard>
      </DiagnosticsSection>

      {isPlatformAdmin ? (
        <DiagnosticsCloudAdminEvidence
          selectedRuntime={selectedRuntime}
          machineInventory={machineInventory}
          traces={traces}
          contractRows={contractRows}
          boundSnapshot={boundSnapshot}
          canonicalError={canonicalError}
          canonicalPending={canonicalPending}
          lifecycleReceipt={lifecycleReceipt}
          lifecycleError={lifecycleError}
          lifecyclePending={lifecyclePending}
        />
      ) : null}
      <DiagnosticsProviderAuthority state={providerAuthority ?? null} />
      <DiagnosticsCodexReadiness readiness={codexReadiness} />
    </>
  );
}
